import ReactDom from 'react-dom';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useCallbackFn } from '@/hooks';
import styles from './index.module.less';

interface ContextMenuOption {
  reference: HTMLElement | { x: number; y: number } | MouseEvent;
  render: () => JSX.Element | JSX.Element[] | React.ReactFragment;
}

const ContextMenu: React.FC<{
  option: ContextMenuOption;
  onRef: (ins: { show: (opt: ContextMenuOption) => void }) => void;
}> = (props) => {
  const elRef = useRef({} as HTMLDivElement);
  const [option, setOption] = useState<ContextMenuOption>(props.option);
  const [showFlag, setShowFlag] = useState<boolean>(false);

  const styleObj = useMemo(() => {
    let top: number, left: number;
    const { reference } = option;
    if ('addEventListener' in reference) {
      const { top: y, left: x } = reference.getBoundingClientRect();
      top = y;
      left = x;
    } else if ('target' in reference) {
      const { clientY: y, clientX: x } = reference;
      top = y;
      left = x;
    } else {
      top = reference.y;
      left = reference.x;
    }
    return {
      top: `${top}px`,
      left: `${left}px`,
      display: showFlag ? 'inline-block' : 'none',
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [option.reference, showFlag]);

  const methods = {
    close: () => {
      setShowFlag(false);
    },
    show: (opt: ContextMenuOption) => {
      setOption(opt);
      setShowFlag(true);
      setTimeout(() => {
        const top = +elRef.current.style.top.replace('px', '');
        const toBottom = document.body.clientHeight - top;
        const height = elRef.current.clientHeight;
        if (toBottom < height) {
          elRef.current.style.top = `${document.body.clientHeight - height - 8}px`;
        }
      }, 0);
    },
  };

  const domClick = useCallbackFn((e: MouseEvent) => {
    if (!elRef.current.contains(e.target as Node)) {
      methods.close();
    } else {
      setTimeout(() => {
        methods.close();
      }, 0);
    }
  });

  props.onRef(methods);

  useEffect(() => {
    document.addEventListener('click', domClick);
    return () => {
      document.removeEventListener('click', domClick);
    };
  }, [domClick]);

  return (
    <div className={styles.context_menu} style={styleObj} ref={elRef}>
      {option.render()}
    </div>
  );
};

const $$contextmenu = (() => {
  let ins: any;
  return (option: ContextMenuOption) => {
    if (!ins) {
      const el = document.createElement('div');
      document.body.appendChild(el);
      ReactDom.render(<ContextMenu option={option} onRef={(i) => (ins = i)} />, el);
    }
    ins.show(option);
    return ins;
  };
})();

export default $$contextmenu;
